import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { inr } from "../lib/api";
import { Package, ChevronRight, LogOut, User, Download } from "lucide-react";

const STATUS_PILL = {
  // New canonical
  pending: "bg-slate-100 text-slate-700",
  processing: "bg-welljio-highlight text-welljio-accent",
  shipped: "bg-welljio-highlight text-welljio-accent",
  delivered: "bg-welljio-soft text-welljio-green",
  cancelled: "bg-rose-50 text-rose-700",
  // Legacy fallbacks
  placed: "bg-slate-100 text-slate-700",
  confirmed: "bg-welljio-highlight text-welljio-accent",
  out_for_delivery: "bg-welljio-soft text-welljio-green",
};
const LEGACY_STATUS_MAP = {
  placed: "pending",
  confirmed: "processing",
  out_for_delivery: "shipped",
};
const displayStatus = (s) => (LEGACY_STATUS_MAP[s] || s || "pending");

export default function AccountPage() {
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/me/orders");
        setOrders(data);
      } finally { setLoading(false); }
    })();
  }, [user]);

  if (checking) return <div className="max-w-3xl mx-auto px-6 py-20">Loading...</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/account" }} replace />;

  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <div data-testid="account-page" className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-start justify-between flex-wrap gap-6 mb-12">
        <div>
          <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-3">Your account</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Hello, {user.name.split(" ")[0]}.</h1>
          <p className="text-welljio-muted mt-2">{user.email}</p>
        </div>
        <button data-testid="logout-button" onClick={handleLogout} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-200 hover:bg-welljio-tint text-sm">
          <LogOut className="w-4 h-4" strokeWidth={1.5}/> Sign out
        </button>
      </div>

      <h2 className="font-heading text-2xl font-semibold mb-6 flex items-center gap-2"><Package className="w-5 h-5" strokeWidth={1.5}/> Your orders</h2>

      {loading ? (
        <div className="grid gap-4">
          {[...Array(3)].map((_,i)=>(<div key={i} className="h-28 bg-welljio-tint rounded-3xl animate-pulse-soft"/>))}
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white border border-slate-100 rounded-3xl p-12 text-center">
          <User className="w-8 h-8 mx-auto mb-3 text-welljio-subtle" strokeWidth={1.5}/>
          <p className="text-welljio-muted mb-6">You haven't placed any orders yet.</p>
          <Link to="/shop" data-testid="account-shop-link" className="inline-block bg-welljio-ink text-white rounded-full px-6 py-3 font-medium hover:bg-slate-800">Start shopping</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <Link
              key={o.id}
              to={`/account/orders/${o.id}`}
              data-testid={`order-row-${o.id}`}
              className="block bg-white border border-slate-100 rounded-3xl p-6 hover:border-welljio-accent transition-colors"
            >
              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="flex gap-3 flex-1 min-w-0">
                  {o.items?.[0]?.image && (
                    <img src={o.items[0].image} alt="" className="w-16 h-16 rounded-2xl object-cover bg-welljio-tint shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-xs text-welljio-subtle">Order {o.id}</p>
                    <p className="font-heading font-semibold mt-1 line-clamp-1">{o.items?.map(i => i.name).join(", ")}</p>
                    <p className="text-xs text-welljio-muted mt-1">Placed {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`text-xs px-3 py-1.5 rounded-full font-medium capitalize ${STATUS_PILL[o.tracking_status || "pending"] || STATUS_PILL.pending}`}>
                    {displayStatus(o.tracking_status)}
                  </span>
                  <span className="font-heading font-semibold">{inr(o.total)}</span>
                  <button
                    type="button"
                    data-testid={`account-download-${o.id}`}
                    title="Download invoice"
                    aria-label="Download invoice"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`/invoice/${o.id}?autoprint=1`, "_blank", "noreferrer"); }}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-slate-200 bg-white text-welljio-ink hover:bg-welljio-tint hover:border-welljio-accent/40 transition-colors"
                  >
                    <Download size={13} strokeWidth={2.2} />
                  </button>
                  <ChevronRight className="w-4 h-4 text-welljio-muted" strokeWidth={1.5}/>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
